<template>
  <div>
    <v-row>
      <v-col cols="12" md="12">
        <v-card class="mx-auto" outlined>
          <v-card-title>
            Produktübersicht
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  v-model="selectedPreistyp"
                  :items="availablePreistypen"
                  label="Preistyp auswählen"
                  @change="loadProductList"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="selectedProdukt"
                  :items="availableProdukte"
                  label="Produkt auswählen"
                  @change="loadPriceData"
                  :disabled="!selectedPreistyp"
                ></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="12" class="d-flex justify-end">
                <v-btn
                  color="primary"
                  @click="exportToCSV"
                  :disabled="!priceData || priceData.length === 0"
                >
                  <v-icon left>mdi-download</v-icon>
                  Als CSV exportieren
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="loading" class="mt-4">
      <v-col cols="12">
        <v-progress-linear indeterminate></v-progress-linear>
      </v-col>
    </v-row>

    <v-row v-if="priceData && priceData.length > 0" class="mt-4">
      <v-col cols="12" md="12">
        <v-card class="mx-auto" outlined>
          <v-card-title>
            Preisdaten Tabelle
            <v-spacer></v-spacer>
            <v-text-field
              v-model="tableSearch"
              append-icon="mdi-magnify"
              label="Suche"
              single-line
              hide-details
            ></v-text-field>
          </v-card-title>
          <v-data-table
            :headers="tableHeaders"
            :items="priceData"
            :items-per-page="25"
            :search="tableSearch"
            class="elevation-1"
          >
            <template v-slot:item.formattedCreated="{ item }">
              {{ formatTimestampToDate(item.created) }}
            </template>
            <template v-slot:item.formattedPrice="{ item }">
              {{ formatPrice(item.price) }}
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="priceData && priceData.length > 0" class="mt-4">
      <v-col cols="12" md="12">
        <v-card class="mx-auto" outlined>
          <v-card-title>
            Preisdiagramm
          </v-card-title>
          <v-card-text>
            <evchart
              v-if="chartData.datasets.length > 0"
              :chartOptions="chartOptions"
              :chartData="chartData"
            ></evchart>
            <div v-else class="text-center pa-4">
              Keine Daten verfügbar
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="errorMessage" class="mt-4">
      <v-col cols="12">
        <v-alert type="error" dismissible @input="errorMessage = ''">
          {{ errorMessage }}
        </v-alert>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { firestore, functions } from "@/plugins/firebase";
import { httpsCallable } from "firebase/functions";
import { getDocs, query, orderBy, collection, doc } from "firebase/firestore";
import evchart from "@/components/evchart.vue";

export default {
  name: "Produktübersicht",
  components: {
    evchart
  },
  data() {
    return {
      selectedPreistyp: null,
      selectedProdukt: null,
      availablePreistypen: ["DFC_Live", "DFC_Schlusskurs", "HFC_Live", "HFC_Schlusskurs"],
      availableProdukte: [],
      priceData: [],
      loading: false,
      tableSearch: "",
      errorMessage: "",
      tableHeaders: [
        { text: "Erstellt (created)", value: "formattedCreated" },
        { text: "Zeitstempel (Dokumentenname)", value: "documentName" },
        { text: "Preis", value: "formattedPrice" }
      ],
      chartOptions: {
        responsive: true,
        maintainAspectRatio: true,
        interaction: {
          intersect: false,
          mode: "index"
        },
        plugins: {
          legend: {
            display: true,
            position: "top"
          },
          tooltip: {
            backgroundColor: "rgba(0,0,0,0.8)",
            titleColor: "#fff",
            bodyColor: "#fff",
            borderColor: "#ddd",
            borderWidth: 1,
            displayColors: true,
            padding: 15,
            caretPadding: 15,
            titleFont: {
              size: 13,
              weight: "bold"
            },
            callbacks: {
              label: function(context) {
                let label = context.dataset.label || "";
                if (label) {
                  label += ": ";
                }
                if (context.parsed.y !== null) {
                  label += parseFloat(context.parsed.y).toLocaleString("de-DE", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  });
                }
                return label;
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: false,
            title: {
              display: true,
              text: "Preis"
            },
            ticks: {
              callback: function(value) {
                return parseFloat(value).toLocaleString("de-DE", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                });
              }
            }
          },
          x: {
            type: "time",
            time: {
              unit: "day",
              displayFormats: {
                day: "dd.MM.yyyy"
              }
            },
            title: {
              display: true,
              text: "Datum"
            }
          }
        }
      }
    };
  },
  computed: {
    chartData() {
      if (!this.priceData || this.priceData.length === 0) {
        return {
          labels: [],
          datasets: []
        };
      }

      const labels = this.priceData.map(entry => {
        if (entry.created && entry.created.toDate) {
          return entry.created.toDate();
        }
        return new Date(entry.created);
      });
      const prices = this.priceData.map(entry => parseFloat(entry.price));

      return {
        labels: labels,
        datasets: [
          {
            label: this.selectedProdukt || "Preis",
            borderColor: "#f87979",
            backgroundColor: "rgba(248, 121, 121, 0.1)",
            borderWidth: 2,
            pointRadius: 4,
            pointBackgroundColor: "#f87979",
            pointBorderColor: "#fff",
            pointBorderWidth: 2,
            pointHoverRadius: 6,
            tension: 0.1,
            data: prices,
            fill: true
          }
        ]
      };
    }
  },
  methods: {
    async loadProductList() {
      if (!this.selectedPreistyp) {
        this.availableProdukte = [];
        this.selectedProdukt = null;
        this.priceData = [];
        return;
      }

      this.loading = true;
      this.errorMessage = "";

      try {
        // Rufe die Cloud Function auf um die Produkte zu laden
        const getProductList = httpsCallable(functions, "getProductList");
        const result = await getProductList({ preistyp: this.selectedPreistyp });

        console.log("Preistyp:", this.selectedPreistyp);
        console.log("Produkte:", result.data.produkte);

        this.availableProdukte = result.data.produkte || [];

        if (this.availableProdukte.length === 0) {
          this.errorMessage = `Keine Produkte für ${this.selectedPreistyp} gefunden`;
        }
      } catch (error) {
        console.error("Fehler beim Laden der Produkte:", error);
        this.errorMessage = `Fehler beim Laden der Produkte: ${error.message}`;
        this.availableProdukte = [];
      } finally {
        this.loading = false;
      }
    },

    async loadPriceData() {
      if (!this.selectedPreistyp || !this.selectedProdukt) {
        this.priceData = [];
        this.errorMessage = "";
        return;
      }

      this.loading = true;
      this.errorMessage = "";

      try {
        // Die Struktur ist: preise/{preistyp}/{produktname}/{timestamp}
        const priceDocRef = doc(firestore, "preise", this.selectedPreistyp);
        const priceCollectionRef = collection(priceDocRef, this.selectedProdukt);

        const q = query(priceCollectionRef, orderBy("created", "asc"));
        const querySnapshot = await getDocs(q);

        // Extrahiere nur die nötigen Felder um Stack Overflow zu vermeiden
        this.priceData = querySnapshot.docs.map(docSnapshot => ({
          documentName: docSnapshot.id,
          created: docSnapshot.data().created,
          price: docSnapshot.data().price
        }));

        if (this.priceData.length === 0) {
          this.errorMessage = `Keine Preisdaten für ${this.selectedProdukt} gefunden`;
        }
      } catch (error) {
        console.error("Fehler beim Laden der Preisdaten:", error);
        this.errorMessage = `Fehler beim Laden der Preisdaten: ${error.message}`;
        this.priceData = [];
      } finally {
        this.loading = false;
      }
    },

    formatTimestampToDate(timestamp) {
      let date;
      
      // Wenn es ein Firestore Timestamp ist
      if (timestamp && typeof timestamp.toDate === "function") {
        date = timestamp.toDate();
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else if (typeof timestamp === "number") {
        date = new Date(timestamp * 1000);
      } else {
        return "N/A";
      }

      return date.toLocaleString("de-DE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      });
    },

    formatPrice(price) {
      // Konvertiere US-amerikanische Schreibweise zu Dezimalzahl
      const num = parseFloat(price.toString().replace(",", "."));
      return num.toLocaleString("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    exportToCSV() {
      if (!this.priceData || this.priceData.length === 0) {
        return;
      }

      // Header
      const headers = ["Erstellt (created)", "Zeitstempel (Dokumentenname)", "Preis"];
      const csvContent = [
        headers.join(";"),
        ...this.priceData.map(row =>
          [
            this.formatTimestampToDate(row.created),
            row.documentName,
            this.formatPrice(row.price)
          ].join(";")
        )
      ].join("\n");

      // CSV-Datei erstellen und herunterladen
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      const filename = `Produktübersicht_${this.selectedPreistyp}_${this.selectedProdukt}_${new Date()
        .toISOString()
        .split("T")[0]}.csv`;
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  },
  async mounted() {
    // SignIn Check
    if (!this.$store.state.auth.isSignedIn) {
      this.$router.push("/login");
      return;
    }
  }
};
</script>

<style scoped>
</style>
